import { Immutable } from "immer";
import { RetailStore } from "tsi-common-react/src/models/location";
import {
    SyncStoreID,
    isoMiles,
    isoPhoneE164,
    isoPostcode,
    isoStateA2,
} from "tsi-common-react/src/models/nominals";
import {
    metersToMiles,
    roundCoordinatePrecision,
} from "tsi-common-react/src/utils/maps";
import { t } from "ttag";

import {
    CMSFlagshipStorePage,
    CMSStoreHourDays,
    EmailLocatorResultsPostData,
    SendStorePostData,
} from "./models";

const getTodayHoursKey = (): CMSStoreHourDays => {
    const date = new Date();
    switch (date.getDay()) {
        case 1:
            return "hours_mon";
        case 2:
            return "hours_tues";
        case 3:
            return "hours_wed";
        case 4:
            return "hours_thurs";
        case 5:
            return "hours_fri";
        case 6:
            return "hours_sat";
        case 0:
            return "hours_sun";
        default:
            throw new Error("Invalid week day");
    }
};

/**
 * Format a time string like "09:00:00" or "14:30:00" into "9AM" or "2:30PM".
 */
const formatTimeString = (timeStr: string): string => {
    const parts = timeStr.split(":");
    let hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    if (minutes !== 0) {
        return `${hours}:${String(minutes).padStart(2, "0")}${ampm}`;
    }
    return `${hours}${ampm}`;
};

/**
 * Get today's hours for a flagship store, checking holiday hours first.
 * If today matches a holiday_hours entry, those hours are shown instead
 * of the regular weekly schedule.
 */
export const getTodayHours = (
    cmsProps: Immutable<CMSFlagshipStorePage>,
): string => {
    const now = new Date();
    const todayStr = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
    ].join("-");

    // Check for holiday hours matching today's date
    const todayHolidays = cmsProps.holiday_hours.filter(
        (h) => h.date === todayStr,
    );
    if (todayHolidays.length > 0) {
        const descriptions = todayHolidays.map((h) => {
            if (h.closed) {
                return t`Closed`;
            }
            if (h.start_time && h.end_time) {
                return `${formatTimeString(h.start_time)} – ${formatTimeString(h.end_time)}`;
            }
            return t`Closed`;
        });
        return descriptions.join(", ");
    }

    // Fall back to regular weekly hours
    return cmsProps[getTodayHoursKey()];
};

export const buildEmailStoreData = (
    stores: Immutable<RetailStore[]>,
    selectedStores: Set<SyncStoreID>,
): EmailLocatorResultsPostData["stores"] => {
    return stores
        .filter((s) => selectedStores.has(s.external_id))
        .map((s) => ({
            id: s.external_id,
            distance:
                s.distance == null
                    ? null
                    : Number(
                          isoMiles.unwrap(metersToMiles(s.distance)).toFixed(2),
                      ),
        }));
};

export const buildStoreData = (
    stores: Immutable<RetailStore[]>,
    selectedStores: Set<SyncStoreID>,
): SendStorePostData["stores"] => {
    return stores
        .filter((s) => {
            return selectedStores.has(s.external_id);
        })
        .map((s) => {
            const destinationAddress = encodeURI(
                [s.address, s.address2, s.city, s.state, s.postal].join(" "),
            );
            return {
                address1: s.address,
                address2: s.address2,
                city: s.city,
                distance: s.distance
                    ? `${isoMiles.unwrap(metersToMiles(s.distance)).toFixed(2)}`
                    : "",
                name: s.name,
                phone: s.phone ? isoPhoneE164.unwrap(s.phone) : "",
                state: isoStateA2.unwrap(s.state),
                url: `https://www.google.com/maps/dir/?daddr=${destinationAddress}`,
                zip: isoPostcode.unwrap(s.postal),
                lat: roundCoordinatePrecision(s.lat),
                lng: roundCoordinatePrecision(s.lng),
            };
        });
};

export const groupStores = (stores: Immutable<RetailStore[]>) => {
    type IStoreGroup = {
        main: Immutable<RetailStore>;
        secondary: Immutable<RetailStore>[];
    };
    const groupMap = new Map<string, IStoreGroup>();
    stores.forEach((store) => {
        if (groupMap.has(store.name)) {
            const group = groupMap.get(store.name)!;
            group.secondary.push(store);
        } else {
            groupMap.set(store.name, {
                main: store,
                secondary: [],
            });
        }
    });
    return Array.from(groupMap.values()).sort((a, b) => {
        if (
            a.main.distance === b.main.distance ||
            !a.main.distance ||
            !b.main.distance
        ) {
            return 0;
        }
        return a.main.distance < b.main.distance ? -1 : 1;
    });
};
